import { ISubscriptionRoleResult } from '../types/subscription-role';
import { ISubscriptionDetail, IIdentityRoleAssignment } from '../types/subscription';
import { IKeysetPaginationResult } from '../types/search';
import { IInvite } from '../types/invite';
import { ISubscriptionAuditEvent } from '../types/audit-event';
import { apiService } from './apiService';

class SubscriptionRoleService {
  /**
   * Search for identities by subscription ID
   * @param subscriptionId - The subscription ID to search for
   * @param cursor - Optional cursor for pagination
   * @param pageSize - Number of items per page (default: 20)
   * @param direction - Pagination direction: 'Forward' or 'Backward' (default: 'Forward')
   * @returns Promise resolving to paginated subscription role results
   */
  public async getIdentitiesBySubscriptionId(
    subscriptionId: string,
    cursor?: string,
    pageSize = 20,
    direction: 'Forward' | 'Backward' = 'Forward'
  ): Promise<IKeysetPaginationResult<ISubscriptionRoleResult>> {
    return apiService.bffClient
      .get<
        IKeysetPaginationResult<ISubscriptionRoleResult>
      >(`/subscriptions/${encodeURIComponent(subscriptionId)}/roleAssignments`, { params: { cursor, pageSize, direction } })
      .then((r) => r.data);
  }

  /**
   * Get role assignments for subscriptions by identity ID
   * @param identityId - The identity ID to get subscriptions for
   * @param cursor - Optional cursor for pagination
   * @param pageSize - Number of items per page (default: 20)
   * @param direction - Pagination direction: 'Forward' or 'Backward' (default: 'Forward')
   * @returns Promise resolving to paginated role assignments with subscription references
   */
  public async getRoleAssignmentsByIdentityId(
    identityId: string,
    cursor?: string,
    pageSize = 20,
    direction: 'Forward' | 'Backward' = 'Forward'
  ): Promise<IKeysetPaginationResult<IIdentityRoleAssignment>> {
    return apiService.bffClient
      .get<IKeysetPaginationResult<IIdentityRoleAssignment>>(`/role-assignments/${identityId}`, {
        params: { cursor, pageSize, direction },
      })
      .then((r) => r.data);
  }

  /**
   * Get subscription details by system and subscription ID
   * @param system - The subscription system
   * @param subscriptionId - The subscription ID
   * @returns Promise resolving to subscription detail
   */
  public async getSubscriptionDetail(
    system: string,
    subscriptionId: string
  ): Promise<ISubscriptionDetail> {
    return apiService.bffClient
      .get<ISubscriptionDetail>(`/subscriptions/${system}/${subscriptionId}`)
      .then((r) => r.data);
  }

  /**
   * Get invites for a subscription
   * @param system - The subscription system
   * @param subscriptionId - The subscription ID
   * @param cursor - Optional cursor for pagination
   * @param pageSize - Number of items per page (default: 20)
   * @param direction - Pagination direction: 'Forward' or 'Backward' (default: 'Forward')
   * @returns Promise resolving to paginated invites
   */
  public async getInvites(
    system: string,
    subscriptionId: string,
    cursor?: string,
    pageSize = 20,
    direction: 'Forward' | 'Backward' = 'Forward'
  ): Promise<IKeysetPaginationResult<IInvite>> {
    return apiService.bffClient
      .get<IKeysetPaginationResult<IInvite>>(`/subscriptions/${system}/${subscriptionId}/invites`, {
        params: { cursor, pageSize, direction },
      })
      .then((r) => r.data);
  }

  /**
   * Create a new invite for a subscription
   * @param system - The subscription system
   * @param subscriptionId - The subscription ID
   * @param email - The email address to invite
   * @returns Promise resolving to the created invite
   */
  public async createInvite(
    system: string,
    subscriptionId: string,
    email: string
  ): Promise<IInvite> {
    return apiService.bffClient
      .post<IInvite>(`/subscriptions/${system}/${subscriptionId}/invites`, {
        roleName: 'Member',
        email,
      })
      .then((r) => r.data);
  }

  /**
   * Delete an invite
   * @param inviteId - The invite ID to delete
   * @returns Promise resolving when the invite is deleted
   */
  public async deleteInvite(inviteId: string): Promise<void> {
    return apiService.bffClient.delete(`/invites/${inviteId}`).then((r) => r.data);
  }

  /**
   * Get role assignments (members) for a subscription
   * @param system - The subscription system
   * @param subscriptionId - The subscription ID
   * @param cursor - Optional cursor for pagination
   * @param pageSize - Number of items per page (default: 20)
   * @param direction - Pagination direction: 'Forward' or 'Backward' (default: 'Forward')
   * @returns Promise resolving to paginated subscription role results
   */
  public async getMemberRoleAssignments(
    system: string,
    subscriptionId: string,
    cursor?: string,
    pageSize = 20,
    direction: 'Forward' | 'Backward' = 'Forward'
  ): Promise<IKeysetPaginationResult<ISubscriptionRoleResult>> {
    return apiService.bffClient
      .get<
        IKeysetPaginationResult<ISubscriptionRoleResult>
      >(`/subscriptions/${system}/${subscriptionId}/roleAssignments`, { params: { cursor, pageSize, direction } })
      .then((r) => r.data);
  }

  /**
   * Get audit events for a subscription
   * @param system - The subscription system
   * @param subscriptionId - The subscription ID
   * @param cursor - Optional cursor for pagination
   * @param pageSize - Number of items per page (default: 20)
   * @param direction - Pagination direction: 'Forward' or 'Backward' (default: 'Forward')
   * @returns Promise resolving to paginated audit events
   */
  public async getAuditEvents(
    system: string,
    subscriptionId: string,
    cursor?: string,
    pageSize = 20,
    direction: 'Forward' | 'Backward' = 'Forward'
  ): Promise<IKeysetPaginationResult<ISubscriptionAuditEvent>> {
    return apiService.bffClient
      .get<
        IKeysetPaginationResult<ISubscriptionAuditEvent>
      >(`/subscriptions/${system}/${subscriptionId}/audit-events`, { params: { cursor, pageSize, direction } })
      .then((r) => r.data);
  }

  /**
   * Delete a member (role assignment) from a subscription
   * @param system - The subscription system
   * @param subscriptionId - The subscription ID
   * @param identityId - The identity ID of the member to remove
   * @param roleName - The role name of the member to remove
   * @returns Promise resolving when the member is deleted
   */
  public async deleteMember(
    system: string,
    subscriptionId: string,
    identityId: string,
    roleName: string
  ): Promise<void> {
    return apiService.bffClient
      .delete(`/subscriptions/${system}/${subscriptionId}/roleAssignments`, {
        params: { identityId, roleName },
      })
      .then((r) => r.data);
  }
}

export const subscriptionRoleService = new SubscriptionRoleService();
